import { onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { asUserId, err, newId, ok, permissionDenied, } from "@pack-it-up/shared";
import { toDomainFriendRequest, toDomainUserProfile } from "../mappers/index.js";
import { logSnapshotError, toDomainError } from "./support.js";
import { callFn } from "./callable.js";
export const makeFriendRepo = (db, p, session, functions) => ({
    // A user's friends live denormalised under users/{me}/friends/{friendId} (Doc 02 §10);
    // the doc id is the friend's userId.
    observeFriends: (userId) => ({
        subscribe(next) {
            const unsub = onSnapshot(p.friends(userId), (snap) => {
                const out = [];
                for (const d of snap.docs) {
                    const r = toDomainUserProfile(d.data(), d.id);
                    if (r.ok)
                        out.push(r.value);
                }
                next(out);
            }, logSnapshotError("friends"));
            return unsub;
        },
    }),
    observeIncomingRequests: (userId) => observeRequests(p, where("toUserId", "==", userId)),
    observeOutgoingRequests: (userId) => observeRequests(p, where("fromUserId", "==", userId)),
    // Exact-email lookup reads the admin-only userLookup table — a Cloud Function (T12).
    async lookupByExactEmail(email) {
        const r = await callFn(functions, "lookupByExactEmail", { email });
        if (!r.ok)
            return r;
        const ref = r.value.ref;
        if (!ref)
            return ok(null);
        return ok({ userId: asUserId(ref.userId), displayName: ref.displayName, ...(ref.photoUrl !== undefined ? { photoUrl: ref.photoUrl } : {}) });
    },
    // Creating a request is a plain client write (rules allow it for the sender).
    async sendRequest(toUserId) {
        const u = session.user();
        if (!u)
            return err(permissionDenied("send a friend request"));
        try {
            const id = newId();
            await setDoc(p.friendRequest(id), {
                id,
                fromUserId: u.id,
                toUserId,
                status: "pending",
                fromProfile: { userId: u.id, displayName: u.displayName },
                createdAt: serverTimestamp(),
            });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // Bilateral friend copies require server privilege — a Cloud Function (T12).
    async acceptRequest(requestId) {
        const r = await callFn(functions, "acceptFriendRequest", { requestId });
        return r.ok ? ok(undefined) : r;
    },
    // The recipient may silently ignore — a plain client write the rules permit.
    async ignoreRequest(requestId) {
        try {
            await updateDoc(p.friendRequest(requestId), { status: "ignored" });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async removeFriend(friendUserId) {
        const r = await callFn(functions, "removeFriend", { friendUserId });
        return r.ok ? ok(undefined) : r;
    },
});
const observeRequests = (p, constraint) => ({
    subscribe(next) {
        const q = query(p.friendRequests(), constraint, where("status", "==", "pending"));
        const unsub = onSnapshot(q, (snap) => {
            const out = [];
            for (const d of snap.docs) {
                const r = toDomainFriendRequest(d.data());
                if (r.ok)
                    out.push(r.value);
            }
            next(out);
        }, logSnapshotError("friendRequests"));
        return unsub;
    },
});
//# sourceMappingURL=friend.repo.js.map